// @/components/reusable/hover-tooltip.jsx
// Reusable hover/focus-triggered tooltip. Wraps an arbitrary trigger element
// (button, icon, etc.) and reveals explanatory content beside it. Built on the
// unified Popover system so positioning + animation match the rest of the app.

import React from 'react';
import clsx from 'clsx';
import { AnimatePresence } from 'motion/react';
import { Popover, PopoverButton, PopoverPanel, useHoverPopover } from './popover';

const widthClasses = {
  sm: 'w-48!',
  md: 'w-64!',
  lg: 'w-80!',
};

/**
 * HoverTooltip — reveals `content` when the user hovers or focuses `children`.
 * The trigger stays the real interactive element; this only adds the popover.
 *
 * @param {React.ReactNode} content        — Explanation shown in the panel
 * @param {React.ReactNode} children       — Trigger element (e.g. a button)
 * @param {string}  [anchor='bottom end']  — Panel anchor position
 * @param {number}  [gap=8]                — Gap between trigger and panel
 * @param {'sm'|'md'|'lg'} [width='sm']    — Panel width
 * @param {string}  [panelClassName]
 */
export function HoverTooltip({
  content,
  children,
  anchor = 'bottom end',
  gap = 8,
  width = 'sm',
  panelClassName,
}) {
  const hover = useHoverPopover();
  if (!content) return children;

  return (
    <Popover className="relative inline-flex">
      <PopoverButton
        as="span"
        className="inline-flex"
        onFocus={hover.onMouseEnter}
        onBlur={hover.onMouseLeave}
        {...hover.handlers}
      >
        {children}
      </PopoverButton>

      <AnimatePresence>
        {hover.isOpen && (
          <PopoverPanel
            hover
            anchor={{ to: anchor, gap }}
            className={clsx(widthClasses[width] || widthClasses.sm, panelClassName)}
            {...hover.handlers}
          >
            <div className="text-xs text-black font-medium [&_p]:mb-2 [&_p:last-child]:mb-0">
              {content}
            </div>
          </PopoverPanel>
        )}
      </AnimatePresence>
    </Popover>
  );
}

export default HoverTooltip;
